import { Service, signal } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { InMemoryStorageProvider, UnleashClient } from 'unleash-proxy-client';

import { IUnleashConfig } from '@ideals/types';

import { switchMapToVoid, VOID } from './utils/switch-map-to-void.util';

const DEFAULT_CONTEXT = 'root';

@Service()
export class FeatureToggleService {
  readonly #clients = new Map<string, UnleashClient>();
  readonly #initialized = signal(false);

  #config?: IUnleashConfig;

  readonly initialized = this.#initialized.asReadonly();

  initialize(config: IUnleashConfig): Observable<void> {
    if (this.#config) {
      throw new Error('FeatureFlags service already initialized');
    }

    this.#config = config;

    return from(this.#getClient(DEFAULT_CONTEXT))
      .pipe(
        tap(() => this.#initialized.set(true)),
        switchMapToVoid,
        catchError(() => VOID),
      );
  }

  isEnabled(flag: string): boolean {
    return this.#clients.get(DEFAULT_CONTEXT)?.isEnabled(flag) ?? false;
  }

  isEnabledWithContext(flag: string, context: string): Observable<boolean> {
    return from(this.#getClient(this.#normalizeContext(context)))
      .pipe(
        map((client) => client.isEnabled(flag)),
        catchError(() => of(false)),
      );
  }

  async #getClient(context: string): Promise<UnleashClient> {
    if (!this.#config) {
      return Promise.reject(new Error('FeatureFlags service is not initialized'));
    }

    if (this.#clients.has(context)) {
      return this.#clients.get(context);
    }

    const client = new UnleashClient({
      appName: this.#config.instanceId,
      clientKey: this.#config.proxySecret,
      disableMetrics: true,
      disableRefresh: true,
      storageProvider: new InMemoryStorageProvider(),
      url: this.#config.url,
    });

    if (context !== DEFAULT_CONTEXT) {
      await client.updateContext({ userId: context });
    }

    await client.start();

    this.#clients.set(context, client);

    return client;
  }

  #normalizeContext(context: string): string {
    const [prefix, domain] = context.split('@');

    return domain
      ? [
        prefix.split('+').pop(),
        domain,
      ].join('@')
      : context;
  }
}
